import * as React from 'react'
import {Typography, Grid, IconButton, Tooltip, Button} from '@mui/material'
import {makeStyles} from '@oc/styles'
import {observer} from 'mobx-react-lite'
import {useIsLoading} from './ViewLoader/ViewLoadContext'
import {useCustomerRouteContext} from './CustomerContext'
import {useGlobalIsDirty} from './dirtyableStateHelper'
import {useGlobalDiscard, useGlobalSave} from './saveableStateHelper'
import {useEventBusChannel} from './EventBus'
import {SaveableStateEvents} from './saveableStateEvents'
import {SaveIcon, CancelIcon, ArrowIcon} from '@oc/icons'
import {messages} from './texts'
import {useValidationState} from './ModulesValidation/ValidationErrorDialog'

export const actionBarHeight = 56

const useStyles = makeStyles(theme => ({
    header: {
        padding: theme.spacing(0, 2)
    },
    headerActionButton: {
        padding: '0px'
    },
    wrapper: {
        overflow: 'auto',
        maxHeight: '100%',
        padding: theme.spacing(2)
    },
    root: {
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: theme.palette.background.default,
        position: 'relative'
    },
    content: {
        height: '100%',
        flexWrap: 'nowrap'
    },
    contentWithAction: {
        height: `calc(100% - ${actionBarHeight}px)`
    },
    bottomNavigation: {
        height: actionBarHeight,
        maxHeight: actionBarHeight,
        display: 'flex',
        backgroundColor: theme.palette.background.paper,
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: 'auto',
        maxWidth: 'calc(100vw - 240px)',
        transition: 'max-width 1s',
        boxShadow: theme.shadows[6],
        margin: theme.spacing(0, 2, 2, 0),
        zIndex: 100,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0)
    },
    buttonClass: {
        width: '150px'
    }
}))

export interface BottomAction {
    label: string
    icon: React.ComponentType
    callback: () => void
    isDisabled?: () => boolean
    key: string | number
    primary?: boolean
}

export interface LayoutProps {
    children?: React.ReactNode
    windowTitle?: string
    header?: string
    headerAction?: () => void
    headerIcon?: React.JSX.Element
    headerActionTooltip?: string
    showLoaderDuringSave?: boolean
    showLoaderDuringDiscard?: boolean
    overrideWrapperClassName?: string
}

interface ActionsProps {
    showLoaderDuringSave?: boolean
    showLoaderDuringDiscard?: boolean
}

function _Actions(props: ActionsProps) {
    const {classes} = useStyles()
    const globalIsDirty = useGlobalIsDirty()
    const validationState = useValidationState()

    const discard = useGlobalDiscard(props.showLoaderDuringDiscard)
    const save = useGlobalSave(props.showLoaderDuringSave)

    React.useEffect(() => {
        validationState._isDirty = globalIsDirty
        if (globalIsDirty) {
            window.addEventListener('beforeunload', validationState.beforeUnload)

            return () => window.removeEventListener('beforeunload', validationState.beforeUnload)
        }
        return () => {}
    }, [validationState, globalIsDirty])

    React.useEffect(() => {
        return () => {
            validationState._isDirty = false
        }
    }, [validationState])

    return (
        <div id="navigation-action-bar" className={classes.bottomNavigation}>
            <Button
                key="discard"
                startIcon={<CancelIcon />}
                disabled={!globalIsDirty}
                onClick={discard}
                color="secondary"
                variant="contained"
                className={classes.buttonClass}
            >
                {messages.getText('layout', 'discard')}
            </Button>

            <Button
                key="save"
                startIcon={<SaveIcon />}
                disabled={!globalIsDirty}
                onClick={save}
                color="primary"
                variant="contained"
                className={classes.buttonClass}
            >
                {messages.getText('layout', 'save')}
            </Button>
        </div>
    )
}
export const Actions = observer(_Actions)

function _Layout(props: LayoutProps) {
    const {
        children,
        header,
        headerAction,
        headerIcon,
        headerActionTooltip,
        windowTitle,
        showLoaderDuringSave,
        showLoaderDuringDiscard,
        overrideWrapperClassName
    } = props
    const {classes, cx} = useStyles()
    const eventBusChannel = useEventBusChannel<SaveableStateEvents>()
    const globalSaveDiscardHasListeners =
        eventBusChannel.listenerCount('save') + eventBusChannel.listenerCount('discard') > 0

    React.useEffect(
        function () {
            if (windowTitle || header) {
                document.title = messages.getText('windowTitle', 'withViewName', windowTitle ?? header)
            } else {
                document.title = messages.getText('windowTitle', 'withoutViewName')
            }
        },
        [windowTitle, header]
    )

    return (
        <NoScrollWhenLoading>
            <Grid
                container
                className={cx(classes.content, {[classes.contentWithAction]: globalSaveDiscardHasListeners})}
                direction="column"
            >
                {typeof header !== 'undefined' ? (
                    <Typography variant="h1" className={classes.header}>
                        {headerAction ? (
                            <Tooltip title={headerActionTooltip || ''}>
                                <IconButton
                                    onClick={headerAction}
                                    color="inherit"
                                    className={classes.headerActionButton}
                                    size="large"
                                >
                                    {headerIcon ? (
                                        headerIcon
                                    ) : (
                                        <ArrowIcon fontSize="inherit" rotate="left" color="inherit" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {header}
                    </Typography>
                ) : null}

                <Grid xs={12} className={overrideWrapperClassName ? overrideWrapperClassName : classes.wrapper} item>
                    {children || null}
                </Grid>
            </Grid>
            {globalSaveDiscardHasListeners ? (
                <Actions
                    showLoaderDuringSave={showLoaderDuringSave}
                    showLoaderDuringDiscard={showLoaderDuringDiscard}
                />
            ) : null}
        </NoScrollWhenLoading>
    )
}
export const Layout = observer(_Layout)

export function NoScrollWhenLoading({children}: React.PropsWithChildren) {
    const isLoading = useIsLoading()
    const {classes} = useStyles()

    return (
        <div id="layout-id" className={classes.root} style={{overflow: isLoading ? 'hidden' : 'auto'}}>
            {children}
        </div>
    )
}

export function useFormattedHeader(base: string, variant: 'customer' | 'rk' | 'base'): string {
    const {accountName, rkName, loading} = useCustomerRouteContext()

    let val

    switch (variant) {
        case 'customer':
            val = accountName
            break
        case 'rk':
            val = rkName
            break
        default:
            return base
    }

    return loading ? `${base} (loading...)` : `${base} (${val})`
}

export default Layout
